import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
// React 19 native metadata
import { ChevronRight, Check, X, Minus, ArrowLeft, ArrowRight } from 'lucide-react';
import ShareButton from '../common/ShareButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WinnerBadge = ({ winner }) => {
  if (winner === 'isupplyai') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium"><Check className="w-3 h-3" /> iSupplyAI</span>;
  if (winner === 'competitor') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-400/15 text-red-400 text-xs font-medium"><X className="w-3 h-3" /> Competitor</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-400 text-xs font-medium"><Minus className="w-3 h-3" /> Tie</span>;
};

export default function ComparisonPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [allComparisons, setAllComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [compRes, allRes] = await Promise.all([
          fetch(`${API_URL}/api/comparisons/${slug}`),
          fetch(`${API_URL}/api/comparisons`),
        ]);
        if (compRes.ok) setData(await compRes.json());
        if (allRes.ok) {
          const d = await allRes.json();
          setAllComparisons(d.comparisons || []);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center text-white">
      <h1 className="text-2xl font-bold mb-4">Comparison not found</h1>
      <Link to="/compare" className="text-purple-400 hover:text-purple-300">View all comparisons</Link>
    </div>
  );

  const wins = { isupplyai: 0, competitor: 0, tie: 0 };
  data.features.forEach(f => { wins[f.winner] = (wins[f.winner] || 0) + 1; });

  const otherComparisons = allComparisons.filter(c => c.slug !== slug);

  // Generate FAQ pairs from feature comparison data
  const faqEntries = data.features.map(f => ({
    "@type": "Question",
    "name": `How does iSupplyAI compare to ${data.competitor_name} for ${f.feature}?`,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": `iSupplyAI: ${f.isupplyai}. ${data.competitor_name}: ${f.competitor}. Winner: ${f.winner === 'isupplyai' ? 'iSupplyAI' : f.winner === 'competitor' ? data.competitor_name : 'Tie'}.`
    }
  }));

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqEntries
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": (data.title || '').slice(0, 110),
    "description": data.meta_description,
    "image": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png",
      "width": 1200,
      "height": 675
    },
    "inLanguage": "en-US",
    "author": {
      "@type": "Organization",
      "name": "iSupplyAI",
      "url": "https://isupplyai.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png",
        "width": 512,
        "height": 512
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "iSupplyAI",
      "url": "https://isupplyai.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png",
        "width": 600,
        "height": 60,
        "caption": "iSupplyAI"
      }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://isupplyai.com/compare/${slug}` },
    "datePublished": "2026-03-12",
    "dateModified": new Date().toISOString().split('T')[0],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://isupplyai.com" },
      { "@type": "ListItem", "position": 2, "name": "Comparisons", "item": "https://isupplyai.com/compare" },
      { "@type": "ListItem", "position": 3, "name": data.title || `iSupplyAI vs ${data.competitor_name}`, "item": `https://isupplyai.com/compare/${slug}` }
    ]
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="comparison-page">
      <>
        <title>{data.title && data.title.length > 60 ? data.title.slice(0, 60) : data.title}</title>
        <meta name="description" content={data.meta_description} />
        <link rel="canonical" href={`https://isupplyai.com/compare/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={data.meta_description} />
        <meta property="og:url" content={`https://isupplyai.com/compare/${slug}`} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content={data.title} />
        <meta name="twitter:description" content={data.meta_description} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </>

      {/* Breadcrumbs */}
      <div className="max-w-5xl mx-auto px-4 pt-8" data-testid="comparison-breadcrumbs">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/compare" className="hover:text-gray-300 transition-colors">Comparisons</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-300">vs {data.competitor_name}</span>
        </nav>
      </div>

      {/* Header */}
      <header className="max-w-5xl mx-auto px-4 pt-8 pb-12">
        <div className="inline-block px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-medium mb-6" data-testid="comparison-category">
          {data.category}
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" data-testid="comparison-title">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">iSupplyAI</span>
          <span className="text-gray-400 mx-3">vs</span>
          <span>{data.competitor_name}</span>
        </h1>
        <p className="text-gray-400 text-lg leading-relaxed max-w-3xl" data-testid="comparison-intro">{data.intro}</p>
      </header>

      {/* Score Summary */}
      <section className="max-w-5xl mx-auto px-4 mb-12" data-testid="comparison-score">
        <div className="grid grid-cols-3 gap-4">
          <div className="p-6 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-center">
            <div className="text-3xl font-bold text-emerald-400">{wins.isupplyai}</div>
            <div className="text-sm text-gray-400 mt-1">iSupplyAI wins</div>
          </div>
          <div className="p-6 rounded-xl bg-gray-500/5 border border-gray-500/20 text-center">
            <div className="text-3xl font-bold text-gray-400">{wins.tie}</div>
            <div className="text-sm text-gray-400 mt-1">Tie</div>
          </div>
          <div className="p-6 rounded-xl bg-red-400/5 border border-red-400/20 text-center">
            <div className="text-3xl font-bold text-red-400">{wins.competitor}</div>
            <div className="text-sm text-gray-400 mt-1">{data.competitor_name} wins</div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="max-w-5xl mx-auto px-4 mb-12" data-testid="comparison-table">
        <h2 className="text-xl font-bold mb-6">Feature-by-Feature Comparison</h2>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-[1fr_2fr_2fr_auto] bg-white/[0.03] px-4 py-3 text-xs font-semibold tracking-wider text-gray-400 uppercase border-b border-white/10">
            <div>Feature</div>
            <div>iSupplyAI</div>
            <div>{data.competitor_name}</div>
            <div className="text-center w-24">Winner</div>
          </div>
          {/* Feature Rows */}
          {data.features.map((f, i) => (
            <div key={i} className={`grid grid-cols-[1fr_2fr_2fr_auto] px-4 py-4 text-sm ${i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'} border-b border-white/5 last:border-0`} data-testid={`comparison-row-${i}`}>
              <div className="font-medium text-white pr-2">{f.feature}</div>
              <div className="text-gray-300 pr-2">{f.isupplyai}</div>
              <div className="text-gray-300 pr-2">{f.competitor}</div>
              <div className="w-24 flex justify-center"><WinnerBadge winner={f.winner} /></div>
            </div>
          ))}
        </div>
      </section>

      {/* Strengths Comparison */}
      <section className="max-w-5xl mx-auto px-4 mb-12" data-testid="comparison-strengths">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
            <h3 className="text-lg font-bold text-emerald-400 mb-4">iSupplyAI Strengths</h3>
            <ul className="space-y-3">
              {data.isupplyai_strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-xl bg-white/[0.03] border border-white/10">
            <h3 className="text-lg font-bold text-gray-300 mb-4">{data.competitor_name} Strengths</h3>
            <ul className="space-y-3">
              {data.competitor_strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Verdict */}
      <section className="max-w-5xl mx-auto px-4 mb-12" data-testid="comparison-verdict">
        <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-900/20 to-cyan-900/10 border border-purple-500/20">
          <h2 className="text-xl font-bold text-white mb-4">The Verdict</h2>
          <p className="text-gray-300 leading-relaxed">{data.verdict}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 mb-12 text-center" data-testid="comparison-cta">
        <h2 className="text-2xl font-bold mb-4">Ready to try multi-agent AI strategy?</h2>
        <p className="text-gray-400 mb-6">See how 12 AI strategists debate your marketing challenges in real time.</p>
        <Link to="/strategy-score" className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl text-sm hover:shadow-lg hover:shadow-purple-500/25 transition-all" data-testid="comparison-cta-btn">
          Get Your Free Strategy Score <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* Related Blog Posts */}
      {data.related_posts && data.related_posts.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 mb-12" data-testid="comparison-related-posts">
          <h2 className="text-xl font-bold mb-6">Related Reading</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.related_posts.map(p => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/30 transition-colors group" data-testid={`related-post-${p.slug}`}>
                <div className="text-xs text-purple-400 mb-1">{p.category}</div>
                <div className="font-medium text-white group-hover:text-purple-300 transition-colors text-sm">{p.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Other Comparisons */}
      {otherComparisons.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 pb-16" data-testid="other-comparisons">
          <h2 className="text-xl font-bold mb-6">More Comparisons</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherComparisons.map(c => (
              <Link key={c.slug} to={`/compare/${c.slug}`} className="p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/30 transition-colors group" data-testid={`other-comparison-${c.slug}`}>
                <div className="text-xs text-gray-500 mb-1">{c.category}</div>
                <div className="font-medium text-white group-hover:text-purple-300 transition-colors">iSupplyAI vs {c.competitor_name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Back link */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <Link to="/compare" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors" data-testid="back-to-comparisons">
          <ArrowLeft className="w-4 h-4" /> All Comparisons
        </Link>
      </div>
      <ShareButton title={data.title} description={data.meta_description} url={`https://isupplyai.com/compare/${slug}`} />
    </div>
  );
}
